import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  User, 
  Briefcase, 
  Phone, 
  Mail, 
  Camera,
  Save,
  Loader2,
  Trash2,
  Eye,
  Upload,
  Building2,
  Globe
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SignatureSettings() {
  const { user, token } = useAuth();
  const { get, put, del, loading } = useApi();
  const fileInputRef = useRef(null);
  
  const [settings, setSettings] = useState({
    signature_name: '',
    signature_title: '',
    signature_tagline: '',
    signature_phone: '',
    signature_email: '',
    signature_company: '',
    signature_website: '',
    signature_headshot_url: null
  });
  
  const [previewHtml, setPreviewHtml] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    // Update preview whenever settings change
    generatePreview();
  }, [settings]);

  const fetchSettings = async () => {
    try {
      const data = await get('/signature/settings');
      setSettings(data);
    } catch (error) {
      console.error('Failed to fetch signature settings:', error);
    }
  };

  const generatePreview = () => {
    const { signature_name, signature_title, signature_tagline, signature_phone, signature_email, signature_company, signature_website, signature_headshot_url } = settings;
    
    // Build full headshot URL
    let headshotUrl = signature_headshot_url;
    if (headshotUrl && headshotUrl.startsWith('/')) {
      headshotUrl = `${API_URL}${headshotUrl}`;
    }
    
    // Debug log
    console.log('Headshot URL:', headshotUrl, 'API_URL:', API_URL, 'Original:', signature_headshot_url);
    
    // Use company name or default
    const companyName = signature_company || 'DH Design';
    
    // Contact line
    const contactParts = [];
    if (signature_phone) contactParts.push(signature_phone);
    if (signature_email) contactParts.push(`<a href="mailto:${signature_email}" style="color: #6B7280; text-decoration: none;">${signature_email}</a>`);
    // Optional website — same gold (#C5A059) as the company name, clickable.
    // Only appended when non-empty so existing signatures render unchanged.
    const websiteRaw = (signature_website || '').trim();
    if (websiteRaw) {
      const hasProtocol = /^(https?:)?\/\//i.test(websiteRaw) || /^(mailto:|tel:|sms:|#|\/)/i.test(websiteRaw);
      const websiteHref = hasProtocol ? websiteRaw : `https://${websiteRaw}`;
      contactParts.push(`<a href="${websiteHref}" target="_blank" rel="noopener noreferrer" style="color: #C5A059; text-decoration: none;">${websiteRaw}</a>`);
    }
    const contactLine = contactParts.join(' • ');
    
    // Headshot HTML
    let headshotHtml = '';
    let leftPadding = '';
    if (headshotUrl) {
      headshotHtml = `
        <td style="vertical-align: top; padding-right: 12px;">
          <img src="${headshotUrl}" alt="${signature_name}" width="60" height="60" style="width: 60px; height: 60px; border-radius: 8px; display: block;" />
        </td>
        <td style="width: 1px; background-color: rgba(197, 160, 89, 0.45); padding: 0;"></td>
      `;
      leftPadding = 'padding-left: 16px;';
    }
    
    const html = `
      <table cellpadding="0" cellspacing="0" border="0" style="font-family: 'Montserrat', Arial, sans-serif; max-width: 600px;">
        <tr>
          ${headshotHtml}
          <td style="vertical-align: top; ${leftPadding}">
            <table cellpadding="0" cellspacing="0" border="0">
              <tr>
                <td style="font-size: 16px; font-weight: 600; color: #1F2937; padding-bottom: 2px;">
                  ${signature_name || 'Your Name'}
                </td>
              </tr>
              <tr>
                <td style="font-size: 13px; color: #6B7280; padding-bottom: 2px;">
                  ${signature_title || 'Your Title'}
                </td>
              </tr>
              ${(signature_tagline || '').trim() ? `
              <tr>
                <td style="font-style: italic; font-size: 12px; color: #6B7280; padding-bottom: 2px;">
                  ${signature_tagline}
                </td>
              </tr>` : ''}
              <tr>
                <td style="font-size: 13px; font-weight: 500; color: #C5A059; padding-bottom: 6px;">
                  ${companyName}
                </td>
              </tr>
              <tr>
                <td style="font-size: 12px; color: #6B7280; padding-bottom: 8px;">
                  ${contactLine || 'Phone • Email'}
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    `;
    
    setPreviewHtml(html);
  };

  const handleSave = async () => {
    if (!settings.signature_name || !settings.signature_email) {
      toast.error('Name and email are required');
      return;
    }
    
    setSaving(true);
    try {
      await put('/signature/settings', {
        signature_name: settings.signature_name,
        signature_title: settings.signature_title,
        signature_tagline: settings.signature_tagline || '',
        signature_phone: settings.signature_phone,
        signature_email: settings.signature_email,
        signature_company: settings.signature_company,
        signature_website: settings.signature_website || ''
      });
      toast.success('Signature settings saved');
    } catch (error) {
      const errorMessage = typeof error === 'string' 
        ? error 
        : error?.message || error?.detail || 'Failed to save settings';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleHeadshotUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      toast.error('Only JPG and PNG images are allowed');
      return;
    }
    
    // Validate file size (5MB max for upload, will be compressed)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/signature/headshot`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }
      
      const data = await response.json();
      setSettings(prev => ({ ...prev, signature_headshot_url: data.headshot_url }));
      toast.success('Headshot uploaded successfully');
    } catch (error) {
      const errorMessage = typeof error === 'string' 
        ? error 
        : error?.message || error?.detail || 'Failed to upload headshot';
      toast.error(errorMessage);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveHeadshot = async () => {
    try {
      await del('/signature/headshot');
      setSettings(prev => ({ ...prev, signature_headshot_url: null }));
      toast.success('Headshot removed');
    } catch (error) {
      toast.error('Failed to remove headshot');
    }
  };

  const getHeadshotUrl = () => {
    if (!settings.signature_headshot_url) return null;
    if (settings.signature_headshot_url.startsWith('/')) {
      return `${API_URL}${settings.signature_headshot_url}`;
    }
    return settings.signature_headshot_url;
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8 animate-fade-in" data-testid="signature-settings-page">
        {/* Header */}
        <div className="pb-6 border-b border-border">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
            Personalization
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl">Signature Settings</h1>
          <p className="text-muted-foreground mt-2">
            Configure your email signature. This will automatically appear at the bottom of every email you send through the CRM.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Settings Form */}
          <Card className="rounded-sm border">
            <CardHeader>
              <CardTitle className="text-lg font-medium">Your Information</CardTitle>
              <CardDescription>
                Enter your details for the email signature
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Headshot Upload */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Camera className="h-4 w-4" />
                  Headshot Photo
                </Label>
                <div className="flex items-center gap-4">
                  {settings.signature_headshot_url ? (
                    <div className="relative">
                      <img 
                        src={getHeadshotUrl()} 
                        alt="Headshot" 
                        className="w-16 h-16 rounded-lg object-cover border"
                      />
                      <Button
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                        onClick={handleRemoveHeadshot}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center border-2 border-dashed">
                      <User className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      onChange={handleHeadshotUpload}
                      className="hidden"
                      id="headshot-upload"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      {settings.signature_headshot_url ? 'Change' : 'Upload'}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-1">
                      JPG or PNG, auto-cropped to square
                    </p>
                  </div>
                </div>
              </div>

              {/* Name */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Full Name *
                </Label>
                <Input
                  value={settings.signature_name}
                  onChange={(e) => setSettings(prev => ({ ...prev, signature_name: e.target.value }))}
                  placeholder="David Hughes"
                  className="rounded-sm"
                  data-testid="sig-name"
                />
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Job Title
                </Label>
                <Input
                  value={settings.signature_title}
                  onChange={(e) => setSettings(prev => ({ ...prev, signature_title: e.target.value }))}
                  placeholder="Growth Systems"
                  className="rounded-sm"
                  data-testid="sig-title"
                />
              </div>

              {/* Tagline (optional) — live-preview only; appears as a single
                  italic line between Job Title and Company Name. Blank = no
                  render at all (no empty row, no placeholder). */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Tagline <span className="text-xs text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Input
                  value={settings.signature_tagline || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, signature_tagline: e.target.value }))}
                  placeholder="Designing systems that scale"
                  className="rounded-sm italic"
                  data-testid="sig-tagline"
                />
              </div>

              {/* Company */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Company Name
                </Label>
                <Input
                  value={settings.signature_company}
                  onChange={(e) => setSettings(prev => ({ ...prev, signature_company: e.target.value }))}
                  placeholder="DH Design"
                  className="rounded-sm"
                  data-testid="sig-company"
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank to use default: DH Design
                </p>
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Phone Number
                </Label>
                <Input
                  type="tel"
                  value={settings.signature_phone}
                  onChange={(e) => setSettings(prev => ({ ...prev, signature_phone: e.target.value }))}
                  placeholder="604-XXX-XXXX"
                  className="rounded-sm"
                  data-testid="sig-phone"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email Address *
                </Label>
                <Input
                  type="email"
                  value={settings.signature_email}
                  onChange={(e) => setSettings(prev => ({ ...prev, signature_email: e.target.value }))}
                  placeholder="david@dhdesign.com"
                  className="rounded-sm"
                  data-testid="sig-email"
                />
              </div>

              {/* Website (optional) — appears on the same line as phone & email,
                  styled in gold, clickable. Blank = signature unchanged. */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Website URL <span className="text-xs text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Input
                  type="url"
                  value={settings.signature_website || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, signature_website: e.target.value }))}
                  placeholder="dhdesign.com"
                  className="rounded-sm"
                  data-testid="sig-website"
                />
                <p className="text-xs text-muted-foreground">
                  Shown in gold on the contact line. Leave blank to omit.
                </p>
              </div>

              {/* Save Button */}
              <div className="pt-4 space-y-2">
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="w-full rounded-sm"
                  data-testid="save-signature-btn"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Signature Settings
                </Button>
                <Button
                  variant="outline"
                  className="w-full rounded-sm"
                  onClick={() => window.open('/email-designer', '_blank')}
                  data-testid="preview-email-btn"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Preview in Email Designer
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Live Preview */}
          <Card className="rounded-sm border">
            <CardHeader>
              <CardTitle className="text-lg font-medium flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Live Preview
              </CardTitle>
              <CardDescription>
                This is how your signature will appear in emails
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div 
                className="bg-white p-6 rounded-sm border"
                style={{ minHeight: '150px' }}
              >
                <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
              </div>
              
              <div className="mt-4 p-4 bg-muted/30 rounded-sm">
                <p className="text-xs text-muted-foreground">
                  <strong>Note:</strong> Your signature will automatically appear at the bottom of every email sent through the CRM Email Designer.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
